import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Box, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import { ConfirmModal } from "../../../../../components/ConfirmModal/ConfirmModal";
import { useToast } from "../../../../../context/ToastContext";
import {
  brand,
  error as errorColors,
  gray,
} from "../../../../../theme/themePrimitives";

const MAX_FILE_SIZE = 1 * 1024 * 1024 * 1024;

interface CsvUploadProps {
  value: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  confirmReupload?: boolean;
}

export const CsvUpload: React.FC<CsvUploadProps> = ({
  value,
  onChange,
  error,
  confirmReupload = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { showToast } = useToast();

  const validateFile = (file: File): boolean => {
    if (!file.name.match(/\.csv$/i)) {
      showToast("Please upload a CSV file (.csv)", "error", 4000);
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      showToast("File size exceeds limit of 1 GB", "error", 4000);
      return false;
    }
    return true;
  };

  const handleFile = (file: File) => {
    if (!validateFile(file)) return;
    if (value && confirmReupload) {
      setPendingFile(file);
      setConfirmOpen(true);
    } else {
      onChange(file);
    }
  };

  const handleConfirmReupload = () => {
    if (pendingFile) onChange(pendingFile);
    setPendingFile(null);
    setConfirmOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      handleFile(e.target.files[0]);
      e.target.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const borderColor = error
    ? errorColors[600]
    : isDragging
      ? brand[400]
      : gray[300];

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        onChange={handleInputChange}
      />
      {value && (
        <Box
          sx={{
            border: `1px solid ${gray[200]}`,
            borderRadius: "12px",
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <InsertDriveFileOutlinedIcon sx={{ color: gray[400] }} />
          <Box minWidth={0}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {value.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(value.size / 1024).toFixed(0)} KB
            </Typography>
          </Box>
        </Box>
      )}
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: `1.5px dashed ${borderColor}`,
          borderRadius: "12px",
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          cursor: "pointer",
          bgcolor: isDragging ? `${brand[50]}` : "transparent",
          transition: "background-color 0.15s, border-color 0.15s",
          minHeight: value ? undefined : 160,
        }}
      >
        <Box
          sx={{
            border: "1px solid #EAECF0",
            borderRadius: "8px",
            p: "10px",
            display: "flex",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <FileUploadOutlinedIcon sx={{ color: gray[700], fontSize: 24 }} />
        </Box>
        <Typography variant="body2" textAlign="center" color="text.secondary">
          <Typography
            component="span"
            variant="body2"
            color={brand[600]}
            fontWeight={500}
            sx={{ "&:hover": { textDecoration: "underline" } }}
          >
            {value ? "Re-upload CSV file" : "Upload CSV file"}
          </Typography>
          {" or drag and drop"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          (max. 1 GB)
        </Typography>
      </Box>
      {error && (
        <Typography
          sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}
          fontSize="12px"
          color="error"
        >
          <ErrorOutlineIcon fontSize="small" /> {error}
        </Typography>
      )}

      <ConfirmModal
        openConfirmModal={confirmOpen}
        setOpenConfirmModal={setConfirmOpen}
        modalTitle="Re-upload CSV file"
        modalSubtitle="Are you sure you want to re-upload a CSV file, as this will delete all previously processed data?"
        confirmButtonClick={handleConfirmReupload}
        confirmButtonText="Yes, I'm sure"
        cancelButtonText="No, I don't want"
      />
    </Box>
  );
};
